import React, { useState } from 'react';
import { Shield } from 'lucide-react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { generateAuditPackage } from '../services/auditService';

interface Props {
  tenantId: string;
}

export const SystemAuditLogs: React.FC<Props> = ({ tenantId }) => {
  const [downloadStatus, setDownloadStatus] = useState<string>('');

  const handleDownloadLogs = async (format: 'json' | 'csv') => {
    if (!tenantId) return;
    setDownloadStatus('Generating...');
    try {
      const auditData = await generateAuditPackage(tenantId);
      
      if (format === 'json') {
        const blob = new Blob([JSON.stringify(auditData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_logs_${tenantId}_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
      } else {
        // For CSV, just download the master_transactions as that's the most common need
        const csvContent = auditData['master_transactions'];
        if (!csvContent) {
            throw new Error("No master transactions found to export as CSV");
        }
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `master_transactions_${tenantId}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
      }
      
      setDownloadStatus('Downloaded!');
      setTimeout(() => setDownloadStatus(''), 3000);
    } catch (err) {
      console.error('Download audit logs failed:', err);
      setDownloadStatus('Error');
      setTimeout(() => setDownloadStatus(''), 3000);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Download Logs Card */}
      <div className="card p-8 bg-white border border-gray-100 hover:border-blue-200 transition-colors group">
        <div className="p-3 bg-blue-50 rounded-xl w-fit text-blue-600 mb-4 group-hover:scale-110 transition-transform">
          <Shield size={24} />
        </div>
        <h3 className="font-bold text-xl text-gray-900 mb-2 font-black uppercase tracking-tight">System Audit Logs</h3>
        <p className="text-sm text-gray-500 mb-6 font-medium">Detailed logs of all configuration changes, staff updates, and role modifications for compliance purposes.</p>
        <div className="flex gap-2">
          <button 
            onClick={() => handleDownloadLogs('json')}
            disabled={downloadStatus === 'Generating...'}
            className="flex-1 py-3 bg-gray-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-gray-800 transition-colors disabled:bg-gray-400"
          >
            {downloadStatus === 'Generating...' ? 'Generating...' : 'JSON'}
          </button>
          <button 
            onClick={() => handleDownloadLogs('csv')}
            disabled={downloadStatus === 'Generating...'}
            className="flex-1 py-3 bg-white border border-gray-300 text-gray-700 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-gray-50 transition-colors disabled:bg-gray-200"
          >
            {downloadStatus === 'Generating...' ? 'Generating...' : 'CSV'}
          </button>
        </div>
      </div>
    </div>
  );
};
